import React, {useRef} from "react";
import {FieldError} from "react-hook-form";
import {
    BaseFormComponent,FormComponent,
    FormType,
    ValidationRuleLevel,
} from "../DynamicFormTypes.ts";
import {AlertCircle} from "lucide-react";
import {FieldInfoIcon} from "./FieldInfoIcon.tsx";
import {toTitleCase} from "../dyanimcFormUtil.tsx";

interface FormLabelProps {
    component: BaseFormComponent;
    classOverride?: string;
}
export const FormLabel: React.FC<FormLabelProps> = ({ component }) => {

    const labelRef = useRef<HTMLLabelElement>(null);

    return (
        <div className="inline-flex w-full">
            <label
                ref={labelRef}
                htmlFor={component.fieldKey}
                className="ml-2 text-gray-700 text-sm max-w-11/12 font-medium group/label"
            >
                {component.label}
            </label>
            <FieldInfoIcon component={component}/>
        </div>
    );
}
export const getColSpan = (width: number | undefined): string => {
    // Default to col-span-1 if width is undefined or invalid
    if (width === undefined || width < 1 || width > 12) {
        return "col-span-1";
    }

    // Explicitly return the full class name string based on width
    switch (width) {
        case 1: return "col-span-1";
        case 2: return "col-span-2";
        case 3: return "col-span-3";
        case 4: return "col-span-4";
        case 5: return "col-span-5";
        case 6: return "col-span-6";
        case 7: return "col-span-7";
        case 8: return "col-span-8";
        case 9: return "col-span-9";
        case 10: return "col-span-10";
        case 11: return "col-span-11";
        case 12: return "col-span-12";
        default: return "col-span-1"; // Fallback to 1 column span
    }
};

interface FormErrorProps {
    fieldState?: {
        invalid: boolean;
        isDirty: boolean;
        isTouched: boolean;
        isValidating: boolean;
        error?: FieldError;
    },
    component : FormComponent;
}

export const getErrorLevel = (error?: FieldError): ValidationRuleLevel | null => {
    if (!error) {
        return null; // No error state
    }

    if (Array.isArray(error)) {
        //for datagrid and component select
        //it will looke like a list of objects, each object has the keys of the fields that failed validation
        let hasError = false;
        for (const err of error) {
            if (err && typeof err === "object") {
                Object.keys(err).forEach(key => {
                    const nestedError = err[key];
                    if (nestedError &&
                        nestedError.type &&
                        typeof nestedError.type === 'string' &&
                        nestedError.type.startsWith(ValidationRuleLevel.ERROR)) {
                        hasError = true;
                    }
                });
            }
        }
        return hasError ? ValidationRuleLevel.ERROR : ValidationRuleLevel.WARNING;
    }

    if (typeof error.type === 'string') {
        return error.type.startsWith(ValidationRuleLevel.WARNING)
            ? ValidationRuleLevel.WARNING
            : ValidationRuleLevel.ERROR;
    }

    // Fallback for non-string types
    return ValidationRuleLevel.ERROR;
};

export const ERROR_SLOT_HEIGHT_CLASS = "h-4";
// const MAX_INLINE_CHARS = 40;

export const FormError: React.FC<FormErrorProps> = ({ fieldState, component }) => {
    const compact = !!component.compactUI;
    const hasError = !!fieldState?.error;

    // No error: compact → render nothing; non-compact → keep spacer for alignment
    if (!hasError) {
        return compact ? null : <div className={`${ERROR_SLOT_HEIGHT_CLASS} mt-1`} aria-hidden />;
    }

    // Compute only when there is an error
    const level = getErrorLevel(fieldState!.error);
    const warning = level === ValidationRuleLevel.WARNING;
    const color = warning ? "text-yellow-600" : "text-red-500";
    const bgColor = warning ? "bg-yellow-600" : "bg-red-600";
    const fullMsg = String((fieldState!.error as any).message ?? "");

    // if (compact) {
        // ICON + TOOLTIP ONLY (no height reserved) — anchor to input wrapper (must be relative)
        let right = "right-2", top = "top-1/2";
        if (component.type === FormType.SELECT) right = "right-8";
        if (component.type === FormType.SIMPLE_DATE) right = "right-7";
        if (component.type === FormType.TEXTFIELD) top = "top-1";
        if (component.type === FormType.CHECKBOX) top = "top-0.5"
        if (component.suffix) { right = "right-12"; top = "top-1"; }
        if (component.suffix && component.type === FormType.NUMBER) { right = "right-16"; top = "top-7"; }

        return (
            <div className={compact ? `absolute ${right} ${top}` : 'relative flex items-start gap-1 mt-1'}>
                <span className="group/errtip">
                     <button
                         type="button"
                         tabIndex={-1}
                         className="p-0.5 outline-none z-10"
                         aria-label={fullMsg}
                         onMouseDown={(e) => e.preventDefault()}
                     >
                         <AlertCircle className={`h-4 w-4 ${color} cursor-help`}/>
                    </button>
                    <div
                        className={`invisible group-hover/errtip:visible group-focus-within/errtip:visible absolute z-50 w-56 ${compact ? '' : 'max-w-fit'} ${component.fieldKey.includes(".") ? 'right-full' : ''} 
                        p-1 mt-1 text-xs text-white ${bgColor} rounded-md shadow-lg`}
                        role="tooltip"
                    >
                        <div className="pl-2 pb-1 font-bold">
                            {toTitleCase(level?.toString() ? level.toString() : "Error")}
                        </div>
                        <div className="bg-white text-black p-2 rounded-xs">
                            {fullMsg}
                        </div>
                    </div>
                </span>
            </div>
        );
};

// Utility function to get common input class names
export const getInputClassNames = (component: BaseFormComponent, fieldState?: {
    invalid: boolean;
    isDirty: boolean;
    isTouched: boolean;
    isValidating: boolean;
    error?: FieldError;
}, disabled?: boolean): string => {
    const errorLevel = fieldState ? getErrorLevel(fieldState.error) : null;
    let allClasses = "block w-full border-1 rounded-md text-base text-xs shadow-sm focus:outline-none focus:ring-1 min-h-lh"

    let borderClasses = ' border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 text-gray-900';

    if (errorLevel === ValidationRuleLevel.ERROR) {
        borderClasses = ' border-red-300 focus:border-red-500 focus:ring-red-500 text-red-900';
    } else if (errorLevel === ValidationRuleLevel.WARNING) {
        borderClasses = ' border-yellow-500 focus:border-yellow-500 focus:ring-yellow-500 text-yellow-900';
    }

    const compactClasses = component.compactUI ? " px-1.5 py-1": " px-3 py-2.5";

    const staticClasses = (component.type === FormType.STATIC_FIELD || disabled)
        ? " bg-gray-50 font-thin"
        : " bg-white";

    const returnClasses = allClasses + borderClasses + compactClasses + staticClasses;

    return returnClasses;
};


export const getWholeDivWrapperClass = (component: BaseFormComponent): string => {
    if (component.compactUI) {
        return COMPACT_WHOLE_DIV_WRAPPER_CLASS;
    }
    return WHOLE_DIV_WRAPPER_CLASS;
}

export const getInputWrapperClass = (component: BaseFormComponent): string => {
    if (component.compactUI) {
        return COMPACT_INPUT_WRAPPER_CLASS;
    }
    return INPUT_WRAPPER_CLASS;
}

const WHOLE_DIV_WRAPPER_CLASS: string = "relative w-full min-w-0 group pr-5";

const COMPACT_WHOLE_DIV_WRAPPER_CLASS: string = "relative mb-2 w-full min-w-0 group";

const INPUT_WRAPPER_CLASS: string = "relative w-full min-w-0";

const COMPACT_INPUT_WRAPPER_CLASS: string = "relative w-full min-w-0";

export const NUMBER_OVERLAY_RIGHT_CLASS =
    "absolute right-8 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none text-sm";

export const NUMBER_OVERLAY_LEFT_CLASS =
    "absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none text-sm";
export const SUFFIX_CLASS: string = "flex-shrink-0 bottom-0 translate-y-1/3 ml-[1em] text-gray-500 pointer-events-none text-sm";

export const LINK_CLASS: string = "block w-full text-center bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2.5 px-4 rounded-md shadow-sm transition duration-150 ease-in-out"
